import type { WritingTask } from './types'

export function taskIndex(tasks: WritingTask[]) {
  const byId = new Map(tasks.map(t => [t.id, t]))
  const children = new Map<string, WritingTask[]>()
  for (const task of tasks) {
    const id = byId.has(task.parentId || '') ? task.parentId! : ''
    if (!children.has(id)) children.set(id, [])
    children.get(id)!.push(task)
  }
  return { byId, children }
}

export function branchIds(tasks: WritingTask[], id: string) {
  const { children } = taskIndex(tasks), result = new Set<string>(), stack = [id]
  while (stack.length) {
    const next = stack.pop()!
    if (result.has(next)) continue
    result.add(next)
    for (const child of children.get(next) || []) stack.push(child.id)
  }
  return result
}

export function organizationRows(tasks: WritingTask[], expanded: Set<string>, search = '') {
  const full = taskIndex(tasks)
  const byId = new Map<string, WritingTask>(), parents = new Map<string, string>(), children = new Map<string, WritingTask[]>()
  // Hide finished/archived rows, while keeping their open descendants reachable.
  // These display parents never change the stored hierarchy or cycle checks.
  const pending = (full.children.get('') || []).map(task => ({ task, parent: '' })).reverse(), visited = new Set<string>()
  while (pending.length) {
    const { task, parent } = pending.pop()!
    if (visited.has(task.id)) continue
    visited.add(task.id)
    const visible = task.status !== 'done' && !task.archived
    if (visible) {
      byId.set(task.id, task); parents.set(task.id, parent)
      if (!children.has(parent)) children.set(parent, [])
      children.get(parent)!.push(task)
    }
    const nested = full.children.get(task.id) || []
    for (let index = nested.length - 1; index >= 0; index--) pending.push({ task: nested[index], parent: visible ? task.id : parent })
  }
  const term = search.trim().toLocaleLowerCase(), visible = new Set<string>()
  if (term) for (const task of byId.values()) {
    if (!task.title.toLocaleLowerCase().includes(term)) continue
    let next: WritingTask | undefined = task
    while (next && !visible.has(next.id)) { visible.add(next.id); next = byId.get(parents.get(next.id) || '') }
  }
  const stack = (children.get('') || []).map(task => ({ task, depth: 0 })).reverse()
  const result: { task: WritingTask; depth: number; hasChildren: boolean }[] = [], seen = new Set<string>()
  while (stack.length) {
    const row = stack.pop()!
    if (seen.has(row.task.id) || (term && !visible.has(row.task.id))) continue
    seen.add(row.task.id)
    const nested = children.get(row.task.id) || []
    result.push({ ...row, hasChildren: nested.length > 0 })
    if (term || expanded.has(row.task.id))
      for (let index = nested.length - 1; index >= 0; index--) stack.push({ task: nested[index], depth: row.depth + 1 })
  }
  return result
}

export function draftTask(title: string, scope: WritingTask['scope'], ownerId: string): WritingTask {
  return { id: 'unsaved-task', title, scope, ownerId, version: 0, details: '', assigneeId: ownerId,
    status: 'todo', archived: false, dueDate: '', projectId: '', targetId: '', createdAt: '', updatedAt: '' }
}
